import { type ComponentType, type KeyboardEvent, useCallback } from 'react';
import { TradingText } from '@trading-ui/core';

import { ThemeUtils } from '@/utils/theme.utils';

import * as styles from './LeftCard.css';

type IconProps = {
  color?: string;
};

type Props = {
  title: string;
  description: string;
  backgroundColor: string;
  textColor: string;
  iconColor: string;
  Icon: ComponentType<IconProps>;
  disabled: boolean;
  onClick: () => void;
};

export default function LeftCard({
  title,
  description,
  backgroundColor,
  textColor,
  iconColor,
  Icon,
  disabled,
  onClick,
}: Props) {
  const currentTextColor = disabled ? 'var(--palette-text-disabled)' : textColor;

  const handleClick = useCallback(() => {
    if (disabled) return;
    onClick();
  }, [disabled, onClick]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      handleClick();
    },
    [handleClick]
  );

  return (
    <div
      className={ThemeUtils.cn(styles.container, disabled && styles.disabled)}
      style={{ backgroundColor }}
      onClick={handleClick}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={handleKeyDown}>
      <div className={styles.iconWrapper}>
        <Icon color={disabled ? 'var(--palette-text-disabled)' : iconColor} />
      </div>
      <div className={styles.content}>
        <TradingText
          text={title}
          type="h5"
          className={ThemeUtils.cn(disabled && styles.disabled)}
          style={{ color: currentTextColor, lineHeight: 1.6 }}
        />
        <TradingText
          text={description}
          type="body1"
          className={ThemeUtils.cn(disabled && styles.disabled)}
          style={{ color: currentTextColor, lineHeight: 1.6 }}
        />
      </div>
    </div>
  );
}
